/**
 * Offline queue using IndexedDB (via idb-keyval).
 * Stores failed mutations and retries them when back online.
 * Falls back to localStorage if IndexedDB is unavailable.
 */
import { get, set, createStore } from 'idb-keyval';

const queueStore = createStore('finiq-offline-queue', 'queue-store');
const QUEUE_KEY = 'actions';
const LS_QUEUE_KEY = 'homie_offline_queue';

export interface QueuedAction {
  id: string;
  type: string;
  payload: Record<string, any>;
  createdAt: string;
  retryCount: number;
}

// In-memory cache for sync reads
let memoryQueue: QueuedAction[] | null = null;

async function loadQueue(): Promise<QueuedAction[]> {
  try {
    const idbQueue = await get<QueuedAction[]>(QUEUE_KEY, queueStore);
    if (idbQueue) {
      memoryQueue = idbQueue;
      return idbQueue;
    }
  } catch {
    // IndexedDB unavailable, fall back
  }
  // Fallback: read from localStorage (migration path)
  try {
    const raw = localStorage.getItem(LS_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    memoryQueue = parsed;
    // Migrate to IDB
    if (parsed.length > 0) {
      await saveQueueToIDB(parsed);
      localStorage.removeItem(LS_QUEUE_KEY);
    }
    return parsed;
  } catch {
    memoryQueue = [];
    return [];
  }
}

async function saveQueueToIDB(queue: QueuedAction[]) {
  memoryQueue = queue;
  try {
    await set(QUEUE_KEY, queue, queueStore);
  } catch {
    // Fallback to localStorage
    localStorage.setItem(LS_QUEUE_KEY, JSON.stringify(queue));
  }
}

/** Synchronous read from memory cache — call initQueue() first */
export function getQueue(): QueuedAction[] {
  return memoryQueue ?? [];
}

/** Async load from IDB — call once on app start */
export async function initQueue(): Promise<QueuedAction[]> {
  return loadQueue();
}

export async function enqueue(type: string, payload: Record<string, any>): Promise<QueuedAction> {
  const action: QueuedAction = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    payload,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  };
  const queue = await loadQueue();
  queue.push(action);
  await saveQueueToIDB(queue);
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
  return action;
}

export async function dequeue(id: string) {
  const queue = (await loadQueue()).filter(a => a.id !== id);
  await saveQueueToIDB(queue);
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
}

export async function incrementRetry(id: string) {
  const queue = (await loadQueue()).map(a =>
    a.id === id ? { ...a, retryCount: a.retryCount + 1 } : a
  );
  await saveQueueToIDB(queue);
}

export async function clearQueue() {
  await saveQueueToIDB([]);
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
}

export function getQueueLength(): number {
  return getQueue().length;
}
